import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAllSchools, createSchool, updateSchool, deleteSchool, SchoolData } from '../services/schoolService';
import { useAuth } from './AuthContext'; // or wherever your auth context is

interface SchoolContextType {
  schools: SchoolData[];
  loading: boolean;
  error: string | null;
  createNewSchool: (schoolData: SchoolData) => Promise<void>;
  updateExistingSchool: (id: number, schoolData: SchoolData) => Promise<void>;
  deleteExistingSchool: (id: number) => Promise<void>;
}

const SchoolContext = createContext<SchoolContextType | undefined>(undefined);

export const useSchoolContext = () => {
  const context = useContext(SchoolContext);
  if (!context) {
    throw new Error('useSchoolContext must be used within a SchoolProvider');
  }
  return context;
};

export const SchoolProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [schools, setSchools] = useState<SchoolData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSchools = async () => {
    setLoading(true);
    try {
      const data = await getAllSchools();
      setSchools(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch schools');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Don't fetch all schools for students, school admin, teachers, and app users - they don't need this data
    const isStudent = user?.roles?.includes('STUDENT');
    const isSchoolAdmin = user?.roles?.includes('SCHOOL_ADMIN');
    const isTeacher = user?.roles?.includes('TEACHER');
    const isAppUser = user?.roles?.includes('APP_USER');
    if (user && !isStudent && !isSchoolAdmin && !isTeacher && !isAppUser) {
      fetchSchools();
    }
  }, [user]);

  const createNewSchool = async (schoolData: SchoolData) => {
    setLoading(true);
    try {
      await createSchool(schoolData);
      await fetchSchools();
      setError(null);
    } catch (err) {
      setError('Failed to create school');
    } finally {
      setLoading(false);
    }
  };

  const updateExistingSchool = async (id: number, schoolData: SchoolData) => {
    setLoading(true);
    try {
      await updateSchool(id, schoolData);
      await fetchSchools();
      setError(null);
    } catch (err) {
      setError('Failed to update school');
    } finally {
      setLoading(false);
    }
  };

  const deleteExistingSchool = async (id: number) => {
    setLoading(true);
    try {
      await deleteSchool(id);
      await fetchSchools();
      setError(null);
    } catch (err) {
      setError('Failed to delete school');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SchoolContext.Provider
      value={{
        schools,
        loading,
        error,
        createNewSchool,
        updateExistingSchool,
        deleteExistingSchool
      }}
    >
      {children}
    </SchoolContext.Provider>
  );
};
